import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { 
  Package, 
  Plus, 
  Minus, 
  AlertTriangle, 
  Edit, 
  Trash2, 
  Search,
  Filter,
  RefreshCw,
  TrendingDown,
  Box
} from 'lucide-react';
import { apiService } from '../../services/api';

const EstoquePage = () => {
  const [itens, setItens] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEntradaModal, setShowEntradaModal] = useState(false);
  const [showSaidaModal, setShowSaidaModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [itemSelecionado, setItemSelecionado] = useState(null);
  const [filtroCategoria, setFiltroCategoria] = useState('todas');
  const [busca, setBusca] = useState('');
  
  const [formData, setFormData] = useState({
    nome: '',
    unidade: 'unidade',
    quantidade: 0,
    quantidade_minima: 5,
    categoria: 'outros',
    preco_unitario: 0
  });
  
  const [movimentacaoData, setMovimentacaoData] = useState({
    item_id: '',
    quantidade: 1
  });

  const categorias = [
    { id: 'todas', nome: 'Todas' },
    { id: 'lubrificantes', nome: 'Lubrificantes' },
    { id: 'filtros', nome: 'Filtros' },
    { id: 'ignição', nome: 'Ignição' },
    { id: 'transmissão', nome: 'Transmissão' },
    { id: 'freios', nome: 'Freios' },
    { id: 'pneus', nome: 'Pneus' },
    { id: 'outros', nome: 'Outros' }
  ];

  const unidades = [
    { id: 'unidade', nome: 'Unidade' },
    { id: 'litro', nome: 'Litro' },
    { id: 'par', nome: 'Par' },
    { id: 'kit', nome: 'Kit' },
    { id: 'metro', nome: 'Metro' }
  ];

  useEffect(() => {
    carregarEstoque();
  }, []);

  const carregarEstoque = async () => {
    try {
      setLoading(true);
      const response = await apiService.getEstoque();
      setItens(response.itens || []);
      setAlertas(response.alertas || []);
    } catch (error) {
      console.error('Erro ao carregar estoque:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async () => {
    try {
      await apiService.addItemEstoque(formData);
      setShowAddModal(false);
      setFormData({
        nome: '',
        unidade: 'unidade',
        quantidade: 0,
        quantidade_minima: 5,
        categoria: 'outros',
        preco_unitario: 0
      });
      carregarEstoque();
    } catch (error) {
      console.error('Erro ao adicionar item:', error);
      alert('Erro ao adicionar item: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditItem = async () => {
    try {
      await apiService.updateItemEstoque(itemSelecionado.id, formData);
      setShowEditModal(false);
      setItemSelecionado(null);
      carregarEstoque();
    } catch (error) {
      console.error('Erro ao editar item:', error);
      alert('Erro ao editar item: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Deseja realmente excluir este item do estoque?')) return;
    
    try {
      await apiService.deleteItemEstoque(itemId);
      carregarEstoque();
    } catch (error) {
      console.error('Erro ao excluir item:', error);
      alert('Erro ao excluir item: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEntrada = async () => {
    try {
      await apiService.registrarEntradaEstoque(movimentacaoData);
      setShowEntradaModal(false);
      setMovimentacaoData({ item_id: '', quantidade: 1 });
      carregarEstoque();
    } catch (error) {
      console.error('Erro ao registrar entrada:', error);
      alert('Erro ao registrar entrada: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSaida = async () => {
    try {
      await apiService.registrarSaidaEstoque(movimentacaoData);
      setShowSaidaModal(false);
      setMovimentacaoData({ item_id: '', quantidade: 1 });
      carregarEstoque();
    } catch (error) {
      console.error('Erro ao registrar saída:', error);
      alert('Erro ao registrar saída: ' + (error.response?.data?.detail || error.message));
    }
  };

  const abrirModalEdicao = (item) => {
    setItemSelecionado(item);
    setFormData({
      nome: item.nome,
      unidade: item.unidade,
      quantidade: item.quantidade,
      quantidade_minima: item.quantidade_minima,
      categoria: item.categoria,
      preco_unitario: item.preco_unitario || 0
    });
    setShowEditModal(true);
  };

  const abrirModalEntrada = (item) => {
    setItemSelecionado(item);
    setMovimentacaoData({ item_id: item.id, quantidade: 1 });
    setShowEntradaModal(true);
  };

  const abrirModalSaida = (item) => {
    setItemSelecionado(item);
    setMovimentacaoData({ item_id: item.id, quantidade: 1 });
    setShowSaidaModal(true);
  };

  const getStatusColor = (item) => {
    if (item.quantidade === 0) return 'bg-red-100 border-red-300 text-red-800';
    if (item.quantidade <= item.quantidade_minima) return 'bg-yellow-100 border-yellow-300 text-yellow-800';
    return 'bg-green-100 border-green-300 text-green-800';
  };

  const getStatusText = (item) => {
    if (item.quantidade === 0) return 'Sem estoque';
    if (item.quantidade <= item.quantidade_minima) return 'Estoque baixo';
    return 'OK';
  };

  const itensFiltrados = itens.filter(item => {
    const matchCategoria = filtroCategoria === 'todas' || item.categoria === filtroCategoria;
    const matchBusca = item.nome.toLowerCase().includes(busca.toLowerCase());
    return matchCategoria && matchBusca;
  });

  const totalItens = itens.reduce((acc, item) => acc + item.quantidade, 0);
  const totalValor = itens.reduce((acc, item) => acc + (item.quantidade * (item.preco_unitario || 0)), 0);

  return (
    <div className="p-6 space-y-6" data-testid="estoque-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Package className="h-7 w-7 text-blue-400" />
            Estoque de Peças
          </h1>
          <p className="text-gray-400 text-sm mt-1">Controle de peças e insumos para manutenção</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={carregarEstoque}
            data-testid="refresh-estoque-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
          <Button 
            className="bg-blue-600 hover:bg-blue-700"
            onClick={() => setShowAddModal(true)}
            data-testid="add-item-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Novo Item
          </Button>
        </div>
      </div>

      {/* Cards de Resumo - Design Premium */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Total de Itens */}
        <div className="group relative overflow-hidden rounded-2xl h-32 bg-gradient-to-br from-blue-500 to-blue-700 shadow-lg hover:shadow-xl hover:shadow-blue-500/20 transition-all">
          <div className="absolute -right-3 -bottom-3 opacity-20 group-hover:opacity-30 transition-opacity">
            <Box className="h-24 w-24 text-white" />
          </div>
          <div className="absolute inset-0 p-5 flex flex-col justify-between">
            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg w-fit">
              <Box className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-3xl font-bold text-white">{itens.length}</p>
              <p className="text-sm text-blue-100">Total de Itens</p>
            </div>
          </div>
        </div>

        {/* Unidades em Estoque */}
        <div className="group relative overflow-hidden rounded-2xl h-32 bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg hover:shadow-xl hover:shadow-emerald-500/20 transition-all">
          <div className="absolute -right-3 -bottom-3 opacity-20 group-hover:opacity-30 transition-opacity">
            <Package className="h-24 w-24 text-white" />
          </div>
          <div className="absolute inset-0 p-5 flex flex-col justify-between">
            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg w-fit">
              <Package className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-3xl font-bold text-white">{totalItens}</p>
              <p className="text-sm text-emerald-100">Unidades em Estoque</p>
            </div>
          </div>
        </div>

        {/* Valor Total */}
        <div className="group relative overflow-hidden rounded-2xl h-32 bg-gradient-to-br from-purple-500 to-violet-600 shadow-lg hover:shadow-xl hover:shadow-purple-500/20 transition-all">
          <div className="absolute -right-3 -bottom-3 opacity-20 group-hover:opacity-30 transition-opacity">
            <TrendingDown className="h-24 w-24 text-white" />
          </div>
          <div className="absolute inset-0 p-5 flex flex-col justify-between">
            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg w-fit">
              <TrendingDown className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">R$ {totalValor.toFixed(2)}</p>
              <p className="text-sm text-purple-100">Valor Total</p>
            </div>
          </div>
        </div>

        {/* Alertas */}
        <div className={`group relative overflow-hidden rounded-2xl h-32 shadow-lg hover:shadow-xl transition-all ${alertas.length > 0 ? 'bg-gradient-to-br from-rose-500 to-red-600 hover:shadow-rose-500/20' : 'bg-gradient-to-br from-slate-600 to-slate-700'}`}>
          <div className="absolute -right-3 -bottom-3 opacity-20 group-hover:opacity-30 transition-opacity">
            <AlertTriangle className="h-24 w-24 text-white" />
          </div>
          <div className="absolute inset-0 p-5 flex flex-col justify-between">
            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg w-fit">
              <AlertTriangle className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-3xl font-bold text-white">{alertas.length}</p>
              <p className="text-sm text-white/80">Alertas</p>
            </div>
          </div>
        </div>
      </div>

      {/* Alertas de Estoque Baixo */}
      {alertas.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-red-800 flex items-center gap-2 text-lg">
              <AlertTriangle className="h-5 w-5" />
              Itens com Estoque Baixo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {alertas.map((alerta, index) => (
                <div 
                  key={index}
                  className={`p-3 rounded-lg border ${alerta.status === 'critico' ? 'bg-red-100 border-red-300' : 'bg-yellow-100 border-yellow-300'}`}
                >
                  <p className="font-medium">{alerta.nome}</p>
                  <p className="text-sm">
                    Quantidade: <span className="font-bold">{alerta.quantidade}</span> / Mínimo: {alerta.quantidade_minima}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtros */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar item..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="pl-10"
              data-testid="search-estoque"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-400" />
          <select
            value={filtroCategoria}
            onChange={(e) => setFiltroCategoria(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
            data-testid="filter-categoria"
          >
            {categorias.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.nome}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Lista de Itens */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {itensFiltrados.map(item => (
            <Card key={item.id} className="hover:shadow-lg transition-shadow" data-testid={`item-${item.id}`}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-semibold text-white">{item.nome}</h3>
                    <p className="text-sm text-gray-400 capitalize">{item.categoria}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(item)}`}>
                    {getStatusText(item)}
                  </span>
                </div>

                <div className="flex items-center justify-between mb-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-white">{item.quantidade}</p>
                    <p className="text-xs text-gray-400">{item.unidade}(s)</p>
                  </div>
                  <div className="text-right text-sm text-gray-400">
                    <p>Mínimo: {item.quantidade_minima}</p>
                    {item.preco_unitario > 0 && (
                      <p>R$ {item.preco_unitario.toFixed(2)}/un</p>
                    )}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 text-emerald-400 border-green-200 hover:bg-green-50"
                    onClick={() => abrirModalEntrada(item)}
                    data-testid={`entrada-${item.id}`}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Entrada
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 text-orange-600 border-orange-200 hover:bg-orange-50"
                    onClick={() => abrirModalSaida(item)}
                    disabled={item.quantidade === 0}
                    data-testid={`saida-${item.id}`}
                  >
                    <Minus className="h-4 w-4 mr-1" />
                    Saída
                  </Button>
                </div>

                <div className="flex gap-2 mt-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="flex-1 text-blue-400"
                    onClick={() => abrirModalEdicao(item)}
                    data-testid={`edit-${item.id}`}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="flex-1 text-rose-400"
                    onClick={() => handleDeleteItem(item.id)}
                    data-testid={`delete-${item.id}`}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Excluir
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {itensFiltrados.length === 0 && !loading && (
        <div className="text-center py-12 text-gray-400">
          <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>Nenhum item encontrado</p>
        </div>
      )}

      {/* Modal Adicionar Item */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adicionar Novo Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome do Item *</Label>
              <Input
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
                placeholder="Ex: Óleo 10W40"
                data-testid="input-nome"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Unidade</Label>
                <select
                  value={formData.unidade}
                  onChange={(e) => setFormData({...formData, unidade: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  {unidades.map(un => (
                    <option key={un.id} value={un.id}>{un.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Categoria</Label>
                <select
                  value={formData.categoria}
                  onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  {categorias.filter(c => c.id !== 'todas').map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.nome}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantidade Inicial</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.quantidade}
                  onChange={(e) => setFormData({...formData, quantidade: parseInt(e.target.value) || 0})}
                  data-testid="input-quantidade"
                />
              </div>
              <div>
                <Label>Quantidade Mínima</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.quantidade_minima}
                  onChange={(e) => setFormData({...formData, quantidade_minima: parseInt(e.target.value) || 0})}
                  data-testid="input-quantidade-minima"
                />
              </div>
            </div>
            <div>
              <Label>Preço Unitário (R$)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formData.preco_unitario}
                onChange={(e) => setFormData({...formData, preco_unitario: parseFloat(e.target.value) || 0})}
                data-testid="input-preco"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleAddItem} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-add-item">
                Adicionar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Item */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome do Item *</Label>
              <Input
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Unidade</Label>
                <select
                  value={formData.unidade}
                  onChange={(e) => setFormData({...formData, unidade: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  {unidades.map(un => (
                    <option key={un.id} value={un.id}>{un.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Categoria</Label>
                <select
                  value={formData.categoria}
                  onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  {categorias.filter(c => c.id !== 'todas').map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.nome}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantidade Atual</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.quantidade}
                  onChange={(e) => setFormData({...formData, quantidade: parseInt(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Quantidade Mínima</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.quantidade_minima}
                  onChange={(e) => setFormData({...formData, quantidade_minima: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
            <div>
              <Label>Preço Unitário (R$)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formData.preco_unitario}
                onChange={(e) => setFormData({...formData, preco_unitario: parseFloat(e.target.value) || 0})}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleEditItem} className="bg-blue-600 hover:bg-blue-700">
                Salvar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Entrada */}
      <Dialog open={showEntradaModal} onOpenChange={setShowEntradaModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-400">
              <Plus className="h-5 w-5" />
              Registrar Entrada
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-400">
              Item: <span className="font-semibold">{itemSelecionado?.nome}</span>
            </p>
            <p className="text-gray-400">
              Estoque atual: <span className="font-semibold">{itemSelecionado?.quantidade}</span>
            </p>
            <div>
              <Label>Quantidade a Adicionar</Label>
              <Input
                type="number"
                min="1"
                value={movimentacaoData.quantidade}
                onChange={(e) => setMovimentacaoData({...movimentacaoData, quantidade: parseInt(e.target.value) || 1})}
                data-testid="input-entrada-quantidade"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowEntradaModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleEntrada} className="bg-green-600 hover:bg-green-700" data-testid="submit-entrada">
                Confirmar Entrada
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Saída */}
      <Dialog open={showSaidaModal} onOpenChange={setShowSaidaModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Minus className="h-5 w-5" />
              Registrar Saída
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-400">
              Item: <span className="font-semibold">{itemSelecionado?.nome}</span>
            </p>
            <p className="text-gray-400">
              Estoque atual: <span className="font-semibold">{itemSelecionado?.quantidade}</span>
            </p>
            <div>
              <Label>Quantidade a Retirar</Label>
              <Input
                type="number"
                min="1"
                max={itemSelecionado?.quantidade || 1}
                value={movimentacaoData.quantidade}
                onChange={(e) => setMovimentacaoData({...movimentacaoData, quantidade: parseInt(e.target.value) || 1})}
                data-testid="input-saida-quantidade"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowSaidaModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaida} className="bg-orange-600 hover:bg-orange-700" data-testid="submit-saida">
                Confirmar Saída
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EstoquePage;
